import React, { useEffect, useRef, useState } from 'react';
import { Switch, Route } from 'react-router-dom';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import Details from './Details';
import OrganizationManagers from './OrganizationManagers';
import OrganizationAdmins from './OrganizationAdmins';
import { translate } from 'components/Translation';
import { DepartmentsService } from 'services/departments/departments';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { useDispatch } from 'react-redux';
import { finalize } from 'rxjs/operators';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';

interface IOrganizationDetailsProps {
	match: {
		url: string;
		path: string;
	};
	account: SingleCorpAccount;
	accounts: SingleCorpAccount[];
}

const OrganizationDetails = (props: IOrganizationDetailsProps) => {
	const { match, account, accounts } = props;
	const [departments, setDepartments] = useState<SingleDepartment[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const dispatch = useDispatch();
	const isMounted = useRef(true);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		dispatch(showLoadingIndicator());
		DepartmentsService.getDepartmentsForCurrentUser()
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
					if (isMounted.current) {
						setLoading(false);
					}
				})
			)
			.subscribe(({ departments }) => {
				if (isMounted.current) {
					setDepartments(departments.filter((department) => department.deptId));
				}
			});
	}, [dispatch]);

	return (
		<div>
			<ConditionalRender
				show={!!account}
				onTrue={() => (
					<BreadcrumbsItem to={props.match.url}>
						{`${account.accountName} - ${translate('path.organization.details')}`}
					</BreadcrumbsItem>
				)}
			/>
			<ConditionalRender
				show={!loading}
				onTrue={() => (
					<Switch>
						<Route
							path={`${match.path}/admins`}
							render={(props: any) => (
								<OrganizationAdmins {...props} departments={departments} accounts={accounts} />
							)}
						/>
						<Route
							path={`${match.path}/managers`}
							render={(props: any) => (
								<OrganizationManagers {...props} departments={departments} accounts={accounts} />
							)}
						/>
						<Route
							path={match.path}
							exact
							render={(props: any) => (
								<Details departments={departments} accounts={accounts} {...props} />
							)}
						/>
					</Switch>
				)}
			/>
		</div>
	);
};

export default OrganizationDetails;
